import type { SwitchOption } from '@/features/dashboard/config';
import { ReportOptions } from '@/features/dashboard/components/ReportOptions';
import { LANG_NAMES, type Lang } from '@/features/survey/i18n';
import type { MinionsPayload } from '../report';
import { useMinionsI18n } from '../i18n';

/** French first, as the design orders them. */
const LANG_ORDER: readonly Lang[] = ['fr', 'en'];

type Props = {
  campaign: MinionsPayload['campaign'];
  isPreview: boolean;
  /** "14:05:12", or null when the report has never been refreshed. */
  updatedAt: string | null;
  onExport: () => void;
  switchOptions: SwitchOption[];
  onSwitch: (path: string) => void;
  isAdmin: boolean;
  onManageUsers: () => void;
  onSignOut: () => void;
};

/** "FR | EN"; each button is named in its own language for screen readers. */
function LangToggle() {
  const { lang, t, setLang } = useMinionsI18n();
  return (
    <div className="mn-lang" role="group" aria-label={t.language}>
      {LANG_ORDER.map((l) => (
        <button key={l} type="button" lang={l} aria-label={LANG_NAMES[l]} aria-pressed={lang === l} onClick={() => setLang(l)}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/** The design's Système U band: badge, title, period, language; then live state, export, report switcher and account actions. */
export function Header(props: Props) {
  const { t, f } = useMinionsI18n();
  const { campaign } = props;
  const state = campaign.live ? t.live : campaign.currentWeek === 0 ? t.notStarted : t.ended;
  return (
    <header className="mn-band">
      <div className="mn-wrap">
        <div className="mn-id">
          <div className="mn-badge" aria-hidden="true">
            Système U<span>Coopérative U</span>
          </div>
          <div>
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
          </div>
        </div>
        <div className="mn-ctrl">
          {props.isPreview && <span className="mn-chip mn-chip-warn">{t.noLiveData}</span>}
          <span className="mn-chip">
            <span className={`mn-dot${campaign.live ? ' on' : ''}`} />
            <b>{state}</b>
            {props.updatedAt && <span className="mn-chip-sub">{t.updatedAt(props.updatedAt)}</span>}
          </span>
          <span className="mn-chip">
            {t.period} <b>{f.period(campaign.start, campaign.end)}</b>
          </span>
          <LangToggle />
        </div>
        <div className="mn-ctrl mn-ctrl-actions">
          <button type="button" className="mn-btn mn-btn-hot" onClick={props.onExport}>
            {t.exportLabel}
          </button>
          {/* `switchOptions` already excludes this report, so one option is a real destination. */}
          {props.switchOptions.length > 0 && (
            <select className="mn-switch" value="" aria-label={t.report} onChange={(e) => e.target.value && props.onSwitch(e.target.value)}>
              <option value="">{t.reportPlaceholder}</option>
              <ReportOptions options={props.switchOptions} labels={{ active: t.reportsActive, archived: t.reportsArchived }} />
            </select>
          )}
          {props.isAdmin && (
            <button type="button" className="mn-btn" onClick={props.onManageUsers}>
              {t.users}
            </button>
          )}
          <button type="button" className="mn-btn" onClick={props.onSignOut}>
            {t.signOut}
          </button>
        </div>
      </div>
    </header>
  );
}
